import React, { useMemo } from "react";
import { ActionButton, Input } from "../../shared";
import { formatCurrency, formatDate } from "../../../utils/formatUtils";
import {
  addCalendarMonthsIso,
  buildInstallmentPreview,
  buildRecurrentPreview,
  type ScheduleRowPreview,
} from "./transactionScheduleUtils";

export interface TransactionScheduleControlsProps {
  disabled: boolean;
  /** When false (ex.: saídas), oculta parcelamento. Padrão: true. */
  showInstallments?: boolean;
  installmentEnabled?: boolean;
  onInstallmentEnabledChange?: (value: boolean) => void;
  recurrentEnabled: boolean;
  onRecurrentEnabledChange: (value: boolean) => void;
  installmentCount?: string;
  onInstallmentCountChange?: (value: string) => void;
  recurrentMonths: string;
  onRecurrentMonthsChange: (value: string) => void;
  dueDate: string;
  competenceDate: string;
  paymentDate: string;
  amountStr: string;
  /** Quando definido, criação parcelada usa estas linhas. Null = calcular no envio. */
  installmentDraftRows?: ScheduleRowPreview[] | null;
  onInstallmentDraftChange?: (rows: ScheduleRowPreview[] | null) => void;
}

const checkboxClass =
  "h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-[#2D3748]";

const dateInputClass =
  "w-full min-w-[9.5rem] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-[#4A5568] dark:bg-[#2D3748] dark:text-slate-100";

const amountInputClass =
  "w-full min-w-[6.5rem] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-right text-gray-900 dark:border-[#4A5568] dark:bg-[#2D3748] dark:text-slate-100";

function relabelInstallmentRows(rows: ScheduleRowPreview[]): ScheduleRowPreview[] {
  const n = rows.length;
  return rows.map((r, i) => ({
    ...r,
    index: i + 1,
    installmentLabel: `${i + 1}/${n}`,
  }));
}

/** Installments: only vencimento is edited in UI — shift that date by one month. */
function shiftInstallmentDueOneMonth(row: ScheduleRowPreview): Pick<ScheduleRowPreview, "dueDate"> {
  return { dueDate: addCalendarMonthsIso(row.dueDate, 1) };
}

export const TransactionScheduleControls: React.FC<TransactionScheduleControlsProps> = ({
  disabled,
  showInstallments = true,
  installmentEnabled = false,
  onInstallmentEnabledChange = () => {},
  recurrentEnabled,
  onRecurrentEnabledChange,
  installmentCount = "2",
  onInstallmentCountChange = () => {},
  recurrentMonths,
  onRecurrentMonthsChange,
  dueDate,
  competenceDate,
  paymentDate,
  amountStr,
  installmentDraftRows = null,
  onInstallmentDraftChange = () => {},
}) => {
  const amountNum = parseFloat(amountStr);
  const instCount = Math.max(0, parseInt(installmentCount, 10) || 0);
  const recMonths = Math.max(0, parseInt(recurrentMonths, 10) || 0);

  const installmentPreview: ScheduleRowPreview[] | null = useMemo(() => {
    if (!installmentEnabled || Number.isNaN(amountNum)) return null;
    return buildInstallmentPreview(amountNum, instCount, dueDate, competenceDate, paymentDate);
  }, [installmentEnabled, amountNum, instCount, dueDate, competenceDate, paymentDate]);

  const recurrentPreview: ScheduleRowPreview[] | null = useMemo(() => {
    if (!recurrentEnabled || Number.isNaN(amountNum)) return null;
    return buildRecurrentPreview(amountNum, recMonths, dueDate, competenceDate, paymentDate);
  }, [recurrentEnabled, amountNum, recMonths, dueDate, competenceDate, paymentDate]);

  const openInstallmentReview = () => {
    const built = buildInstallmentPreview(
      amountNum,
      instCount,
      dueDate,
      competenceDate,
      paymentDate
    );
    if (built?.length) {
      onInstallmentDraftChange(relabelInstallmentRows(built));
    }
  };

  const recalcInstallmentDraftFromForm = () => {
    const built = buildInstallmentPreview(
      amountNum,
      instCount,
      dueDate,
      competenceDate,
      paymentDate
    );
    if (built?.length) {
      onInstallmentDraftChange(relabelInstallmentRows(built));
    }
  };

  const updateDraftRow = (position: number, patch: Partial<ScheduleRowPreview>) => {
    if (!installmentDraftRows) return;
    const next = installmentDraftRows.map((r, i) =>
      i === position ? { ...r, ...patch } : r
    );
    onInstallmentDraftChange(relabelInstallmentRows(next));
  };

  const handleDraftDueChange = (position: number, value: string) => {
    updateDraftRow(position, { dueDate: value.trim() });
  };

  const handleDraftAmountChange = (position: number, value: string) => {
    const n = parseFloat(value.replace(",", "."));
    if (value.trim() === "" || Number.isNaN(n)) {
      updateDraftRow(position, { amount: 0 });
      return;
    }
    updateDraftRow(position, { amount: n });
  };

  const handleShiftRowOneMonth = (position: number) => {
    if (!installmentDraftRows?.[position]) return;
    updateDraftRow(position, shiftInstallmentDueOneMonth(installmentDraftRows[position]));
  };

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 dark:border-[#374151] bg-gray-50/50 dark:bg-[#1A202C]/40 p-4">
      <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
        {showInstallments ? "Parcelamento e recorrência" : "Recorrência"}
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start">
        {showInstallments && (
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-slate-300">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={installmentEnabled}
              disabled={disabled}
              onChange={(e) => {
                const on = e.target.checked;
                onInstallmentEnabledChange(on);
                if (on) onRecurrentEnabledChange(false);
                if (!on) onInstallmentDraftChange(null);
              }}
            />
            Parcelado (divide o valor)
          </label>
        )}
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-slate-300">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={recurrentEnabled}
            disabled={disabled}
            onChange={(e) => {
              const on = e.target.checked;
              onRecurrentEnabledChange(on);
              if (on && showInstallments) {
                onInstallmentEnabledChange(false);
                onInstallmentDraftChange(null);
              }
            }}
          />
          Recorrente (mesmo valor todo mês)
        </label>
      </div>

      {showInstallments && installmentEnabled && !disabled && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3 max-w-2xl">
            <div className="flex-1 min-w-[160px] max-w-xs">
              <Input
                label="Número de parcelas"
                type="number"
                min={2}
                max={120}
                value={installmentCount}
                onChange={(e) => onInstallmentCountChange(e.target.value)}
                disabled={disabled}
                placeholder="Ex: 12"
              />
            </div>
            <ActionButton
              type="button"
              variant="blue"
              size="sm"
              onClick={openInstallmentReview}
              disabled={
                !dueDate.trim() ||
                Number.isNaN(amountNum) ||
                instCount < 2 ||
                !installmentPreview?.length
              }
              className="shrink-0 py-2.5"
            >
              Revisar parcelas
            </ActionButton>
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            A data de <strong>vencimento</strong> acima é a 1ª parcela; as demais somam um mês cada. O valor total
            começa dividido igualmente; em <strong>Revisar parcelas</strong> você pode ajustar vencimento, valor de
            cada parcela e usar <strong>+1 mês</strong> para adiar só aquela parcela.
          </p>
        </div>
      )}

      {recurrentEnabled && !disabled && (
        <div className="max-w-xs">
          <Input
            label="Número de meses"
            type="number"
            min={2}
            max={120}
            value={recurrentMonths}
            onChange={(e) => onRecurrentMonthsChange(e.target.value)}
            disabled={disabled}
            placeholder="Ex: 12"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            Serão criadas transações com o mesmo valor, vencendo todo mês a partir da data de vencimento.
          </p>
        </div>
      )}

      {showInstallments && installmentEnabled && installmentDraftRows && installmentDraftRows.length > 0 && !disabled && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Edição das parcelas</p>
            <ActionButton
              type="button"
              variant="link"
              onClick={recalcInstallmentDraftFromForm}
              className="!text-blue-600 hover:!text-blue-800 dark:!text-blue-400 dark:hover:!text-blue-300 text-xs"
            >
              Recalcular a partir do formulário
            </ActionButton>
            <ActionButton
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => onInstallmentDraftChange(null)}
              className="!p-0 h-auto min-h-0 !rounded-none border-0 shadow-none text-xs font-medium text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Fechar edição
            </ActionButton>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748]/30">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-[#374151] text-left text-gray-600 dark:text-slate-400">
                  <th className="px-2 py-2 font-medium">#</th>
                  <th className="px-2 py-2 font-medium">Parcela</th>
                  <th className="px-2 py-2 font-medium">Vencimento</th>
                  <th className="px-2 py-2 font-medium text-right">Valor (R$)</th>
                  <th className="px-2 py-2 font-medium w-24">Ajuste</th>
                </tr>
              </thead>
              <tbody>
                {installmentDraftRows.map((row, position) => (
                  <tr
                    key={`${row.index}-${position}`}
                    className="border-b border-gray-100 dark:border-[#374151]/80 last:border-0 align-top"
                  >
                    <td className="px-2 py-2 text-gray-800 dark:text-slate-200">{row.index}</td>
                    <td className="px-2 py-2 text-gray-800 dark:text-slate-200 whitespace-nowrap">
                      {row.installmentLabel ?? "—"}
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="date"
                        className={dateInputClass}
                        value={row.dueDate}
                        onChange={(e) => handleDraftDueChange(position, e.target.value)}
                        title="Vencimento"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        step={0.01}
                        min={0}
                        className={amountInputClass}
                        value={Number.isFinite(row.amount) ? row.amount : ""}
                        onChange={(e) => handleDraftAmountChange(position, e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <ActionButton
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => handleShiftRowOneMonth(position)}
                        tooltip="Adiciona um mês ao vencimento desta parcela"
                        className="whitespace-nowrap !px-2 !py-1 text-xs dark:border-[#4A5568] dark:bg-[#374151] dark:text-slate-200 dark:hover:bg-[#4A5568]"
                      >
                        +1 mês
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInstallments &&
        installmentEnabled &&
        (!installmentDraftRows || installmentDraftRows.length === 0) &&
        installmentPreview &&
        installmentPreview.length > 0 &&
        !disabled && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748]/30">
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400 border-b border-gray-100 dark:border-[#374151]">
              Próximos vencimentos e valores — clique em <strong>Revisar parcelas</strong> para editar.
            </p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-[#374151] text-left text-gray-600 dark:text-slate-400">
                  <th className="px-3 py-2 font-medium">#</th>
                  <th className="px-3 py-2 font-medium">Parcela</th>
                  <th className="px-3 py-2 font-medium">Vencimento</th>
                  <th className="px-3 py-2 font-medium text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {installmentPreview.map((row) => (
                  <tr
                    key={row.index}
                    className="border-b border-gray-100 dark:border-[#374151]/80 last:border-0 text-gray-800 dark:text-slate-200"
                  >
                    <td className="px-3 py-2">{row.index}</td>
                    <td className="px-3 py-2">{row.installmentLabel ?? "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.dueDate)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {recurrentEnabled && recurrentPreview && recurrentPreview.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748]/30">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#374151] text-left text-gray-600 dark:text-slate-400">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Vencimento</th>
                <th className="px-3 py-2 font-medium">Competência</th>
                <th className="px-3 py-2 font-medium">Pagamento</th>
                <th className="px-3 py-2 font-medium text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {recurrentPreview.map((row) => (
                <tr
                  key={row.index}
                  className="border-b border-gray-100 dark:border-[#374151]/80 last:border-0 text-gray-800 dark:text-slate-200"
                >
                  <td className="px-3 py-2">{row.index}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.dueDate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.competenceDate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.paymentDate)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatCurrency(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {((showInstallments && installmentEnabled) || recurrentEnabled) && !dueDate.trim() && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          Informe a data de vencimento para gerar a prévia e criar as transações.
        </p>
      )}
    </div>
  );
};
